import React, { Component, Suspense } from 'react';
import classNames from 'classnames';
import { flowRight } from 'lodash';
import { type WithTranslation } from '@wix/yoshi-flow-editor';
import { PlayArrow } from '../../../common/components/icons/play-arrow';
import { PlayIcon } from '../../../common/components/icons/play-icon';
import ImageLoader from '../../../common/components/image-loader';
import withDeviceType, { type WithDeviceType } from '../../../common/hoc/with-device-type';
import withEventEmitter, { type WithEventEmitter } from '../../../common/hoc/with-event-emitter';
import withMediaHosts, { type WithMediaHosts } from '../../../common/hoc/with-media-hosts';
import withTranslate from '../../../common/hoc/with-translate';
import { getVideoEmbedThumbUrl } from '../../../common/services/get-video-embed-thumb-url';
import { buildUrl } from '../../../common/services/video-embed';
import styles from './video-embed.scss';

const ReactPlayer = React.lazy(
  () => import(/* webpackChunkName: "react-player" */ './react-player-wrapper'),
);

const MAX_WAIT_TIME = 5000;

const getStyle = (oembed: Oembed) => ({
  backgroundImage: `url(${getVideoEmbedThumbUrl(oembed)})`,
});

type Oembed = Parameters<typeof getVideoEmbedThumbUrl>[0] & {
  video_url?: string;
};

type OwnProps = {
  oembed: Oembed;
  alt?: string;
  className?: string;
};

type Props = OwnProps &
  WithDeviceType &
  WithEventEmitter &
  WithMediaHosts &
  Pick<WithTranslation, 't'>;

type State = {
  isLoading: boolean;
  isLoaded: boolean;
};

type Player = React.ComponentRef<typeof ReactPlayer>;

class VideoEmbed extends Component<Props, State> {
  player: Player | null = null;

  timeout?: ReturnType<typeof setTimeout>;

  state: State = {
    isLoading: false,
    isLoaded: false,
  };

  componentDidMount() {
    this.props.eventEmitter.on('video-start', this.handleVideoStart);
  }

  componentWillUnmount() {
    this.props.eventEmitter.removeListener('video-start', this.handleVideoStart);
    clearTimeout(this.timeout);
  }

  setPlayer = (player: Player | null) => {
    this.player = player;
  };

  handleVideoStart = (player: Player | null) => {
    if (this.player !== player) {
      this.setState({
        isLoading: false,
        isLoaded: false,
      });
    }
  };

  handlePlay = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ isLoading: true });
    setTimeout(() => this.handleReady(), MAX_WAIT_TIME);
  };

  handleReady = () => {
    if (this.state.isLoading) {
      this.setState({ isLoaded: true });
    }
  };

  handleStart = () => {
    this.props.eventEmitter.emitEvent('video-start', [this.player]);
  };

  renderPlayIcon = () => (
    <span>
      <PlayIcon className={styles.icon} />
    </span>
  );

  renderThumbnail() {
    const { isLoading, isLoaded } = this.state;
    const { isMobile, isDesktop, t, oembed, alt } = this.props;
    const { video_url } = oembed;

    return (
      !isLoaded && (
        <div
          className={styles.thumbnail}
          style={getStyle(oembed)}
          onClick={this.handlePlay}
          role="img"
          aria-label={alt}
        >
          <div className={styles.overlay} />
          {!isLoading && isMobile && video_url && (
            <div className={styles.button}>
              <PlayArrow className={styles.arrow} /> {t('video-embed.load-video')}
            </div>
          )}
          {!isLoading && isDesktop && this.renderPlayIcon()}
          {isLoading && <ImageLoader type="medium" />}
        </div>
      )
    );
  }

  renderPlayer() {
    const { isLoading } = this.state;
    const { oembed, isDesktop, videoHost } = this.props;

    return (
      isLoading && (
        <Suspense fallback={null}>
          <ReactPlayer
            ref={this.setPlayer}
            className={styles.player}
            width="100%"
            height="100%"
            url={buildUrl(oembed.video_url ?? '', videoHost ?? '')}
            onReady={this.handleReady}
            onStart={this.handleStart}
            playing={isDesktop}
            controls
          />
        </Suspense>
      )
    );
  }

  render() {
    const { className } = this.props;

    return (
      <div className={classNames(styles.videoEmbed, className)}>
        {this.renderPlayer()}
        {this.renderThumbnail()}
      </div>
    );
  }
}

export default flowRight(
  withTranslate,
  withDeviceType,
  withEventEmitter,
  withMediaHosts,
)(VideoEmbed);
